// The page an invite or password link opens (#/join?token=…). On an invite the person chooses a
// username (checked as they type) and a password. If the link wasn't emailed to them, they first
// give their email and confirm it with a 6-digit code Gold sends, so every login has a confirmed
// address for password resets. Then they're signed in to the simulator.
import { api } from "./api";
import { esc } from "./format";

export async function renderJoin(app: HTMLElement, token: string, done: () => void) {
  document.body.classList.add("signed-out");
  document.body.classList.remove("in-sim", "sim-user");
  app.innerHTML = `<div class="signin card"><div class="loading" role="status"><span></span>Checking your link…</div></div>`;
  let link: { kind: "invite" | "reset"; email: string | null; username: string | null; needsEmail: boolean };
  try {
    link = await api("auth/link", "POST", { token });
  } catch (e) {
    app.innerHTML = `<div class="signin card"><h1>Gold</h1><p class="error">${esc((e as Error).message)}</p><a href="#/">Go to sign in</a></div>`;
    return;
  }
  const invite = link.kind === "invite";
  app.innerHTML = `
    <form class="signin card" id="join" novalidate>
      <h1>${invite ? "Welcome to Gold" : "New password"}</h1>
      <p class="muted">${invite ? "You've been invited to Gold's share-trading simulator: practise trading ASX shares with pretend money at real, slightly delayed prices." : "Choose a new password for your Gold login."}</p>
      ${link.needsEmail ? `
        <label>Your email<input name="email" type="email" autocomplete="email" value="${esc(link.email ?? "")}" required></label>
        <div class="join-code"><button type="button" class="ghost" id="send-code">Email me a code</button><span class="small muted" id="code-note">We'll send a 6-digit code to confirm it's yours. It's also how you reset your password later.</span></div>
        <label id="code-row" hidden>Code from the email<input name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]{6}"></label>`
      : link.email ? `<p class="small">Email: <b>${esc(link.email)}</b></p>` : ""}
      ${invite ? `<label>Username<input name="username" value="${esc(link.username ?? "")}" autocapitalize="none" spellcheck="false" maxlength="20" autocomplete="username" required></label>
        <p class="small muted" id="u-note">3–20 letters, numbers, dots, dashes or underscores. Shown on the leaderboard; you can change it later.</p>` : `<p class="small">Username: <b>${esc(link.username ?? "")}</b></p>`}
      <label>Password<input name="password" type="password" minlength="10" autocomplete="new-password" required></label>
      <label>Password again<input name="again" type="password" minlength="10" autocomplete="new-password" required></label>
      <p class="small muted">At least 10 characters.</p>
      <div class="error" id="join-error"></div>
      <button type="submit" class="primary">${invite ? "Join" : "Save password"}</button>
    </form>`;
  const form = document.getElementById("join") as HTMLFormElement;
  const err = document.getElementById("join-error")!;
  const field = (n: string) => form.querySelector<HTMLInputElement>(`[name=${n}]`);

  // Username: checked as they type.
  const uname = field("username");
  const note = document.getElementById("u-note");
  let timer = 0;
  uname?.addEventListener("input", () => {
    clearTimeout(timer);
    const v = uname.value.trim();
    timer = window.setTimeout(async () => {
      if (!note) return;
      if (!/^[A-Za-z0-9][A-Za-z0-9._-]{2,19}$/.test(v)) { note.textContent = "3–20 letters, numbers, dots, dashes or underscores, starting with a letter or number."; note.className = "small muted"; return; }
      try {
        const r = await api<{ free: boolean }>("auth/username", "POST", { token, username: v });
        note.textContent = r.free ? `“${v}” is free.` : `“${v}” is taken. Try another.`;
        note.className = `small ${r.free ? "up" : "down"}`;
      } catch (e) { note.textContent = (e as Error).message; note.className = "small down"; }
    }, 350);
  });

  // Email code.
  document.getElementById("send-code")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const codeNote = document.getElementById("code-note")!;
    b.disabled = true;
    try {
      const r = await api<{ sentTo: string }>("auth/code", "POST", { token, email: field("email")!.value });
      codeNote.textContent = `Code sent to ${r.sentTo}. Check your inbox (and spam). It lasts 30 minutes.`;
      document.getElementById("code-row")!.hidden = false;
      field("code")!.focus();
      b.textContent = "Send another code";
    } catch (x) { codeNote.textContent = (x as Error).message; codeNote.className = "small down"; }
    b.disabled = false;
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    if (link.needsEmail && !String(f.get("code") ?? "").trim()) { err.textContent = "Tap “Email me a code” and enter the code from the email."; return; }
    if (String(f.get("password")).length < 10) { err.textContent = "Use a password of at least 10 characters."; return; }
    if (f.get("password") !== f.get("again")) { err.textContent = "The two passwords don't match."; return; }
    const b = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    b.disabled = true;
    try {
      await api("auth/accept", "POST", {
        token, password: f.get("password"),
        ...(invite ? { username: f.get("username") } : {}),
        ...(link.needsEmail ? { email: f.get("email"), code: f.get("code") } : {}),
      });
      history.replaceState(null, "", "#/simulator");
      done();
    } catch (x) {
      err.textContent = (x as Error).message;
      b.disabled = false;
    }
  });
}
